import React from 'react'
import { Checkbox, Badge, Button } from '@/shared/ui'
import type { SubscriptionItem } from '../types'
import type { InvoiceHistoryRow } from '../services/subscriptionHistoryService'
import { format } from '@/shared/utils/date'
import { ChevronDown } from 'lucide-react'
import { formatSubscriptionCurrency, formatSubscriptionIntervalLabel } from '../utils/format'

export type SubscriptionCardFuturisticProps = {
  item: SubscriptionItem
  checked: boolean
  onToggle: (checked: boolean) => void
  expanded: boolean
  onToggleExpand: () => void
  history?: { rows: InvoiceHistoryRow[]; total: number }
}

export default function SubscriptionCardFuturistic({
  item,
  checked,
  onToggle,
  expanded,
  onToggleExpand,
  history,
}: SubscriptionCardFuturisticProps) {
  const firstHistory = history?.rows?.[0]
  const displayPlanName = firstHistory?.plan_name || item.plan_name || item.plan_id || '-'
  const displayAmountCents =
    (firstHistory?.amount_paid_cents && firstHistory.amount_paid_cents > 0
      ? firstHistory.amount_paid_cents
      : item.amount_cents || 0)
  const displayCurrency = item.currency || firstHistory?.currency || 'BRL'
  const rawInterval = (item.interval || firstHistory?.interval || '')?.toString().toLowerCase()
  const displayInterval = formatSubscriptionIntervalLabel(rawInterval)

  const statusNode = (() => {
    const raw = String(item.status || '').toLowerCase()
    const isPending = raw.includes('incomplete')
    const isCanceled = raw === 'canceled'
    const isActive = raw === 'active'
    const label = isPending
      ? 'Pendente'
      : isCanceled
      ? 'Cancelado'
      : isActive
      ? 'Aprovado'
      : raw === 'past_due'
      ? 'Vencida'
      : raw === 'trialing' || item.is_trial
      ? 'Teste'
      : raw || '-'
    const color = isPending
      ? 'info'
      : isCanceled
      ? 'error'
      : raw === 'active'
      ? 'success'
      : raw === 'trialing' || item.is_trial
      ? 'warning'
      : raw === 'past_due'
      ? 'warning'
      : 'default'
    return (
      <Badge variant="soft" color={color as any} className="px-3 py-1 text-xs font-medium uppercase tracking-wide">
        {label}
      </Badge>
    )
  })()

  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 shadow-sm">
      <div className="grid grid-cols-[minmax(260px,1.2fr)_minmax(200px,260px)_minmax(140px,180px)_minmax(260px,1.1fr)_minmax(140px,160px)_42px] items-center gap-4 px-5 py-4">
        <div className="flex items-center gap-3">
          <Checkbox
            aria-label={`Selecionar ${item.customer_email || item.customer_name || item.account_id}`}
            checked={checked}
            onChange={(e) => onToggle((e.target as HTMLInputElement).checked)}
          />
          {item.avatar_url ? (
            <img
              src={item.avatar_url}
              alt={item.customer_name || item.customer_email || 'avatar'}
              className="h-10 w-10 rounded-full object-cover border border-[#161616]"
              onError={(e: any) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            <div className="h-10 w-10 flex items-center justify-center rounded-full bg-emerald-500/10 text-[11px] font-semibold text-emerald-300 ring-1 ring-emerald-400/40">
              {(item.customer_name || item.customer_email || '?').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-sm font-semibold leading-tight">{item.customer_name || '-'}</div>
            <div className="text-[11px] text-foreground/70 leading-tight">{item.customer_email || '-'}</div>
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold">{displayPlanName}</div>
          <div className="text-[11px] text-foreground/70">
            {formatSubscriptionCurrency(displayAmountCents, displayCurrency)}
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold">{displayInterval}</div>
          <div className="text-[11px] text-foreground/70">renovação</div>
        </div>

        <div className="text-[11px]">
          <div>
            <span className="text-foreground/70 font-medium">Criado:</span>{' '}
            {item.created_at ? format(item.created_at) : '-'}
          </div>
          <div>
            <span className="text-foreground/70 font-medium">Próx. venc:</span>{' '}
            {item.current_period_end ? format(item.current_period_end) : '-'}
          </div>
        </div>

        <div className="flex items-center justify-start">{statusNode}</div>

        <div className="flex justify-end">
          <Button
            variant="ghost"
            className={`h-8 w-8 p-0 transition-transform duration-200 hover:bg-foreground/5 ${
              expanded ? 'rotate-180' : ''
            }`}
            onClick={onToggleExpand}
            aria-label={expanded ? 'Ocultar histórico' : 'Exibir histórico'}
          >
            <ChevronDown className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-4">
          <div className="rounded-xl border border-[#161616] bg-background/40 p-4">
            <div className="mb-3 text-sm font-semibold">Linha do tempo de faturas</div>
            <div className="grid grid-cols-[minmax(220px,1.1fr)_minmax(160px,220px)_minmax(120px,140px)_minmax(120px,140px)_minmax(140px,160px)_minmax(160px,1fr)] gap-3 text-[11px] text-foreground/90 pb-2">
              <div className="text-foreground/70">Plano</div>
              <div className="text-foreground/70">Valor</div>
              <div className="text-foreground/70">Intervalo</div>
              <div className="text-foreground/70">Tipo</div>
              <div className="text-foreground/70">Criada</div>
              <div className="text-foreground/70">Próx. vencimento</div>
            </div>
            {(history?.rows || []).map((h) => (
              <div
                key={h.id}
                className="grid grid-cols-[minmax(220px,1.1fr)_minmax(160px,220px)_minmax(120px,140px)_minmax(120px,140px)_minmax(140px,160px)_minmax(160px,1fr)] items-center gap-3 text-[11px] py-1"
              >
                <div>{h.plan_name || item.plan_name || item.plan_id || '-'}</div>
                <div className="text-foreground/70">
                  {formatSubscriptionCurrency(
                    h.amount_paid_cents || displayAmountCents,
                    h.currency || displayCurrency,
                  )}
                </div>
                <div>{formatSubscriptionIntervalLabel(h.interval || item.interval)}</div>
                <div>
                  <Badge variant="soft" className="text-[10px] uppercase tracking-wide">
                    renovação
                  </Badge>
                </div>
                <div>{h.created_at ? format(h.created_at) : '-'}</div>
                <div className="text-foreground/70">{h.period_end ? format(h.period_end) : '-'}</div>
              </div>
            ))}
            {!history?.rows?.length && (
              <div className="py-3 text-center text-sm text-foreground/70">
                Sem histórico para esta assinatura.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
